import React, { useContext, useState, useMemo, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { CirclePlus, Search } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '../../components/ui/pagination';
import { Link } from 'react-router-dom';
import { ApiContext } from './../context/AdminContext.jsx';
import { Toaster } from 'sonner';

const ServiceProvider = () => {
    const { partners, setPartner } = useContext(ApiContext);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Filter approved partners
    const approvedPartners = useMemo(() => {
        return partners?.filter((partner) => partner?.Profile?.KYC?.status === 'approved') || [];
    }, [partners]);

    // Filter partners based on search term
    const filteredPartners = useMemo(() => {
        if (!searchTerm) return approvedPartners;
        
        const lowercasedSearch = searchTerm.toLowerCase();
        return approvedPartners.filter(partner => 
            partner?.Profile?.name?.toLowerCase().includes(lowercasedSearch) ||
            partner?.Profile?.phone?.includes(searchTerm) ||
            partner?.Profile?.email?.toLowerCase().includes(lowercasedSearch) ||
            partner?.Profile?.address?.toLowerCase().includes(lowercasedSearch) ||
            partner?.Profile?.id?.toLowerCase().includes(lowercasedSearch) ||
            new Date(partner?.Profile?.createdAt).toLocaleDateString().includes(searchTerm)
        );
    }, [approvedPartners, searchTerm]);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    // Calculate pagination based on filtered partners
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredPartners.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredPartners.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString();
    };
    // console.log("partners : " , partners)

      if (!partners || partners?.length === 0) {
     return<div className="flex items-center justify-center h-screen">
           <button
  disabled=""
  class="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-6 rounded-full shadow-lg flex items-center transition duration-300 transform hover:scale-105 active:scale-95"
>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    class="animate-spin h-5 w-5 mr-3 text-white"
  >
    <circle
      stroke-width="4"
      stroke="currentColor"
      r="10"
      cy="12"
      cx="12"
      class="opacity-25"
    ></circle>
    <path
      d="M4 12a8 8 0 018-8v8H4z"
      fill="currentColor"
      class="opacity-75"
    ></path>
  </svg>
  Loading...
</button>
    </div>
  }
    return (
        <div className='px-[1vw] my-5'>
            <Toaster />
            <div className='flex lg:justify-between lg:items-center flex-col lg:flex-row'>
                <h1 className='text-2xl'>Verified Providers ({filteredPartners.length})</h1>
                <div className='flex lg:gap-4 items-center gap-2 my-3 lg:my-0'>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input 
                            placeholder="Search providers..."
                            className="pl-10 w-full lg:w-64"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <Separator className='my-4' />
            
            <div className='overflow-x-auto lg:max-w-full h-[65vh]'>
                {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <p>Loading providers...</p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[50px]">Sl No.</TableHead>
                                <TableHead>Provider Name</TableHead>
                                <TableHead>Reg Date</TableHead>
                                <TableHead>Phone No</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Address</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Provider ID</TableHead>
                                <TableHead>Agent Name</TableHead>
                                <TableHead>Details</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {currentItems.length > 0 ? (
                                currentItems.map((partner, index) => (
                                    <TableRow key={partner?.Profile?.id || index}>
                                        <TableCell>{indexOfFirstItem + index + 1}.</TableCell>
                                        <TableCell>{partner?.Profile?.name || "N/A"}</TableCell>
                                        <TableCell>{formatDate(partner?.Profile?.createdAt)}</TableCell>
                                        <TableCell>{partner?.Profile?.phone || "N/A"}</TableCell>
                                        <TableCell className="truncate max-w-[150px]">
                                            {partner?.Profile?.email || "N/A"}
                                        </TableCell>
                                        <TableCell className="truncate max-w-[150px]">
                                            {partner?.Profile?.address || "N/A"}
                                        </TableCell>
                                        <TableCell>
                                            <span className='text-blue-600 bg-blue-100 p-1 rounded-md text-xs'>
                                                {partner?.Profile?.KYC?.status || "N/A"}
                                            </span>
                                        </TableCell>
                                        <TableCell className="truncate">
                                            <span className='text-green-700 bg-green-100 p-1 rounded-md text-xs'>
                                                {partner?.Profile?.id || "N/A"}
                                            </span>
                                        </TableCell>
                                        <TableCell className="truncate max-w-[150px]"> 
                                                {partner?.Profile?.agentName || "N/A"} 
                                        </TableCell>
                                        <TableCell>
                                            <Link 
                                                to={`/admin/serviceProviderDetails/${partner?.Profile?.id}`}
                                                onClick={() => setPartner(partner)}
                                            >
                                                <Button variant="ghost" size="sm">
                                                    View
                                                </Button>
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">
                                        {searchTerm ? "No matching providers found" : "No providers available"}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </div>

            {filteredPartners.length > itemsPerPage && (
                <div className="mt-4">
                    <Pagination>
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious 
                                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                />
                            </PaginationItem>

                            {Array.from({ length: totalPages }).map((_, index) => {
                                const page = index + 1;
                                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                    return (
                                        <PaginationItem key={page}>
                                            <PaginationLink
                                                isActive={page === currentPage}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                                return null;
                            })}

                            <PaginationItem>
                                <PaginationNext 
                                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default ServiceProvider;